import type * as React from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import type { TenantId } from '@etiquetachef/types';
import type { UsageDocResult } from '@/lib/firestoreTenantDetail';
import { UsageCard } from './UsageCard';
import { UsageHistoryTable } from './UsageHistoryTable';
import { useUsageHistory } from './useUsageHistory';

type Props = {
  tenantId: TenantId;
  usage: UsageDocResult;
  yyyymm: string;
};

/**
 * Aba "Uso" do detalhe do tenant: mês corrente + histórico mensal.
 *
 * Origem: sem issue — commit 3a98de728, 2026-04-26 17:24 +0200 (aba Uso, Sprint 2).
 *
 * Usado por: `routes/TenantDetailPage.tsx` (aba Uso), que já traz `usage` do
 * mês corrente pelo detalhe; o histórico é assinado aqui (`useUsageHistory`).
 */
export function UsageTab({ tenantId, usage, yyyymm }: Props): React.JSX.Element {
  const { t } = useTranslation();
  const history = useUsageHistory(tenantId);

  return (
    <SpaceBetween size="m">
      <UsageCard usage={usage} yyyymm={yyyymm} />

      <Container header={<Header variant="h2">{t('tenantDetail.usageHistory.title')}</Header>}>
        {history.kind === 'LOADING' && <Spinner />}
        {history.kind === 'ERROR' && (
          <Alert type="error">{t('tenantDetail.usageHistory.errorLoad')}</Alert>
        )}
        {history.kind === 'READY' && (
          <UsageHistoryTable entries={history.entries} currentYyyymm={yyyymm} />
        )}
      </Container>
    </SpaceBetween>
  );
}
